import { type MouseEvent, useCallback, useEffect, useState } from 'react';

import { type Bet } from '~/components/BettingInsights/types';
import { useGeolocationContext } from '~/vendor/cx-web/feature-geolocation/geolocation-context';
import { useRepubContext } from '~/vendor/cx-web/feature-repub';
import { pollForWindowProperty } from '~/vendor/cx-web/utils/pollForWindowObject';
import { postOddsClick } from '~/vendor/cx-web/utils/postOddsClick';

export const useOCRepubBetslip = ({
  bet,
  onClick,
}: {
  bet: Bet;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
}) => {
  const { repub, repubIs } = useRepubContext();
  const { countryCode, state } = useGeolocationContext();
  const [isBetInBetslip, setIsBetInBetslip] = useState(false);

  useEffect(() => {
    if (!repubIs.OC) return;

    const handleBetAdded = (e: Event) => {
      const customEvent = e as CustomEvent<{ betId: number }>;
      if (customEvent.detail.betId === bet.betId) {
        setIsBetInBetslip(true);
      }
    };

    const handleBetRemoved = (e: Event) => {
      const customEvent = e as CustomEvent<{ betId: number }>;
      if (customEvent.detail.betId === bet.betId) {
        setIsBetInBetslip(false);
      }
    };

    const handleCleared = () => {
      setIsBetInBetslip(false);
    };

    window.addEventListener('oc-betslip:bet-added', handleBetAdded);
    window.addEventListener('oc-betslip:bet-removed', handleBetRemoved);
    window.addEventListener('oc-betslip:cleared', handleCleared);

    const initState = async () => {
      await pollForWindowProperty('ocBetslip');
      window.ocBetslip && setIsBetInBetslip(window.ocBetslip?.isInBetslip(bet.betId));
    };
    initState();

    return () => {
      window.removeEventListener('oc-betslip:bet-added', handleBetAdded);
      window.removeEventListener('oc-betslip:bet-removed', handleBetRemoved);
      window.removeEventListener('oc-betslip:cleared', handleCleared);
    };
  }, [repubIs, bet.betId]);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      // Bound to the resolved widget rather than optional-chained per call: the CDN script loads
      // async, and while it is absent every mutation below no-ops while the beacon would still fire,
      // reporting a bet that was never added.
      const betslip = window.ocBetslip;

      if (betslip) {
        if (betslip.isInBetslip(bet.betId)) {
          betslip.removeBet({ betId: bet.betId });
        } else {
          betslip.addBet(bet);
          betslip.toggle();
          // The non-OC path gets this from betslip-context's addOrRemoveFromBetslip; the CDN widget
          // this hook drives has no equivalent, so OC has to send it here.
          postOddsClick({
            betId: bet.betId,
            bookmakerCode: bet.bookmakerCode,
            countryCode,
            repub,
            subdivisionCode: state,
          });
        }
      }

      onClick?.(e);
    },
    [onClick, bet, countryCode, repub, state],
  );

  return {
    isBetInBetslip,
    handleClick,
  };
};
